// Abuse (plan step 5.4; D56 item 7): patterns worth a look, never an automatic action. Several
// trials from one company's email domain in 30 days, trials that hit their cap within a day,
// one card (Stripe's fingerprint, never the number) behind several organizations, and apps
// whose records carry another app name than their bundle id (a key copied into another build).
// Each row opens the organization. It names customers, so staff say why first: the reason goes
// with the request into the admin audit log.
// Console D (APS-412 G1): the reason in a form window under the head, then one table per signal,
// each section with its own empty, not-configured or error line.
import { ErrorState as ErrorLine, PageHead, Section } from '@apsio/ui'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { type FormEvent, useState } from 'react'
import { abuseQuery } from '../api/queries'
import { DataTable, FormWindow, NotConfigured, ReasonField } from '../components/kit'
import { Empty, ErrorState, Loading } from '../components/states'
import { useI18n } from '../i18n'

function OrgLinks({ orgs }: { orgs: { id: string; name: string }[] }) {
  return (
    <ul className="stack" style={{ gap: 2 }}>
      {orgs.map((o) => (
        <li key={o.id}>
          <Link to="/orgs/$orgId" params={{ orgId: o.id }} translate="no">
            {o.name}
          </Link>
        </li>
      ))}
    </ul>
  )
}

export function AbusePage() {
  const { t } = useI18n()
  const [reason, setReason] = useState('')
  const [draft, setDraft] = useState('')
  const navigate = useNavigate()
  if (reason) return <AbuseSignals reason={reason} />
  function start(e: FormEvent) {
    e.preventDefault()
    if (draft.trim()) setReason(draft.trim())
  }
  return (
    <main id="main" className="pg">
      <PageHead title={t('abuse.title')} meta={t('abuse.lede')} />
      <FormWindow
        title={t('abuse.start')}
        submitLabel={t('abuse.start')}
        onSubmit={start}
        onCancel={() => navigate({ to: '/overview' })}
      >
        <ReasonField value={draft} onChange={setDraft} hint={t('abuse.why')} />
      </FormWindow>
    </main>
  )
}

function AbuseSignals({ reason }: { reason: string }) {
  const { t, number, dateTime } = useI18n()
  const abuse = useQuery(abuseQuery(reason))
  return (
    <main id="main" className="pg">
      <PageHead title={t('abuse.title')} meta={t('abuse.lede')} />
      {abuse.isPending ? (
        <Loading />
      ) : abuse.isError ? (
        <ErrorState error={abuse.error} retry={() => abuse.refetch()} />
      ) : (
        <>
          <Section title={t('abuse.domainsTitle')} meta={t('abuse.domainsSub')}>
            <p className="g1-def">{t('abuse.domainsNote')}</p>
            {abuse.data.trials_by_domain.length === 0 ? (
              <Empty title={t('abuse.domainsNone')} />
            ) : (
              <DataTable label={t('table.of', { name: t('abuse.domainsTitle') })} minWidth={560}>
                <thead>
                  <tr>
                    <th scope="col">{t('abuse.col.domain')}</th>
                    <th scope="col">{t('abuse.col.organizations')}</th>
                  </tr>
                </thead>
                <tbody>
                  {abuse.data.trials_by_domain.map((d) => (
                    <tr key={d.domain}>
                      <td className="cell-mono" translate="no">
                        {d.domain}
                      </td>
                      <td>
                        <OrgLinks orgs={d.organizations} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            )}
          </Section>
          <Section title={t('abuse.capsTitle')} meta={t('abuse.capsSub')}>
            <p className="g1-def">{t('abuse.capsNote')}</p>
            {abuse.data.fast_caps.length === 0 ? (
              <Empty title={t('abuse.capsNone')} />
            ) : (
              <DataTable label={t('table.of', { name: t('abuse.capsTitle') })} minWidth={600}>
                <thead>
                  <tr>
                    <th scope="col">{t('abuse.col.organization')}</th>
                    <th scope="col">{t('abuse.col.started')}</th>
                    <th scope="col">{t('abuse.col.capped')}</th>
                  </tr>
                </thead>
                <tbody>
                  {abuse.data.fast_caps.map((f) => (
                    <tr key={`${f.org_id}-${f.period_start}`}>
                      <td>
                        <OrgLinks orgs={[{ id: f.org_id, name: f.name }]} />
                      </td>
                      <td>{dateTime(f.period_start)}</td>
                      <td>{dateTime(f.capped_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            )}
          </Section>
          <Section title={t('abuse.cardsTitle')} meta={t('abuse.cardsSub')}>
            <p className="g1-def">{t('abuse.cardsNote')}</p>
            {abuse.data.shared_cards.length === 0 ? (
              <Empty title={t('abuse.cardsNone')} />
            ) : (
              <DataTable label={t('table.of', { name: t('abuse.cardsTitle') })} minWidth={560}>
                <thead>
                  <tr>
                    <th scope="col">{t('abuse.col.card')}</th>
                    <th scope="col">{t('abuse.col.organizations')}</th>
                  </tr>
                </thead>
                <tbody>
                  {abuse.data.shared_cards.map((c) => (
                    <tr key={c.fingerprint_end}>
                      <td className="cell-mono" translate="no">
                        {`…${c.fingerprint_end}`}
                      </td>
                      <td>
                        <OrgLinks orgs={c.organizations} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            )}
          </Section>
          <Section title={t('abuse.appsTitle')} meta={t('abuse.appsSub')}>
            <p className="g1-def">{t('abuse.appsNote')}</p>
            {abuse.data.unexpected_apps.status === 'not_configured' ? (
              <NotConfigured missing={t('abuse.appsNotConfigured')} />
            ) : abuse.data.unexpected_apps.status === 'unavailable' ? (
              <ErrorLine message={t('health.unavailable')} />
            ) : (abuse.data.unexpected_apps.data ?? []).length === 0 ? (
              <Empty title={t('abuse.appsNone')} />
            ) : (
              <DataTable label={t('table.of', { name: t('abuse.appsTitle') })} minWidth={680}>
                <thead>
                  <tr>
                    <th scope="col">{t('abuse.col.organization')}</th>
                    <th scope="col">{t('abuse.col.bundle')}</th>
                    <th scope="col">{t('abuse.col.seen')}</th>
                    <th scope="col" className="num">
                      {t('abuse.col.records')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(abuse.data.unexpected_apps.data ?? []).map((a) => (
                    <tr key={`${a.app_id}-${a.service_name}`}>
                      <td>
                        <OrgLinks orgs={[{ id: a.org_id, name: a.org_name }]} />
                      </td>
                      <td className="cell-mono" translate="no">
                        {a.bundle_id}
                      </td>
                      <td className="cell-mono" translate="no">
                        {a.service_name}
                      </td>
                      <td className="num">{number(a.records)}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            )}
          </Section>
        </>
      )}
    </main>
  )
}
